import React, { useEffect, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Box, Paper, Tabs, Tab, Typography } from "@mui/material"
import { PreviewNavbar } from "./PreviewNavbar"
import { PreviewHero } from "./PreviewHero"
import { FeaturedArticles } from "./FeaturedArticles"
import { BrowseCategory } from "./BrowseCategory"
import { getAllKbCategories } from "../../../../store/features/getCategoriesToKnowledgeBase"
import { fetchWorkspaceSettings } from "../../../../store/features/workspaceSettingsApi"
import { buildExternalKbUrl, selectBusinessSlug } from "../../../../utils/kbPublicUrl"



// viewMode: "web" | "tab" | "mobile"
const getViewMode = (currentTab) => {
    switch (currentTab) {
        case 1: return "tab";
        case 2: return "mobile";
        default: return "web";
    }
};

const getPreviewMaxWidth = (viewMode) => {
    switch (viewMode) {
        case "tab": return "580px";
        case "mobile": return "321.07px";
        default: return "100%";
    }
};

export const PreviewSection = ({ settings, currentTab, onTabChange, loading, groupId }) => {
    const dispatch = useDispatch();
    const {
        articles: featuredArticles,
        loading: featuredLoading,
        hasFetched: featuredHasFetched,
        lastFetchedBusinessId,
    } = useSelector((state) => state.featuredArticles);
    const { data: categoriesData, loading: categoriesLoading } = useSelector((state) => state.getAllKbCategories);
    const business_id = useSelector((state) => state.loginToken?.response?.user?.business_id);
    const businessSlug = useSelector(selectBusinessSlug);
    const featuredReadyForCurrentBusiness =
        featuredHasFetched &&
        String(lastFetchedBusinessId) === String(business_id);
    const shouldShowFeaturedLoading =
        Boolean(business_id) && (featuredLoading || !featuredReadyForCurrentBusiness);


    useEffect(() => {
        if (business_id && groupId) {
            dispatch(getAllKbCategories({ group_id: groupId }));
        }
    }, [dispatch, business_id, groupId]);

    useEffect(() => {
        if (business_id && !businessSlug) {
            dispatch(fetchWorkspaceSettings());
        }
    }, [dispatch, business_id, businessSlug]);

    const publicCategories = useMemo(
        () => (categoriesData?.data || []).filter((c) => c.visibility === 'public'),
        [categoriesData?.data]
    );

    // Tenant = business detail slug (stable); only group_id changes per group
    const handleLiveView = () => {
        const publicKBUrl = buildExternalKbUrl({
            slug: businessSlug,
            groupId,
        });
        if (!publicKBUrl) return;
        window.open(publicKBUrl, "_blank");
    };

    const viewMode = getViewMode(currentTab);
    const previewMaxWidth = getPreviewMaxWidth(viewMode);

    return (
        <Paper
            sx={{
                flex: 1,
                borderRadius: '8px',
                border: "1px solid #E5E9EC",
                backgroundColor: "#F9FAFA",
                p: '25px',
                boxShadow: 'none',
                height: "100%",
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Tabs
                        value={currentTab}
                        onChange={(e, newValue) => onTabChange(newValue)}
                        sx={{
                            minHeight: 'auto',
                            "& .MuiTab-root": {
                                fontSize: 12,
                                fontWeight: 500,
                                color: "#405261",
                                textTransform: "none",
                                minWidth: "auto",
                                minHeight: 'auto',
                                mr: 3,
                                p: 0,
                                pb: '8px',
                                fontFamily: "Inter",
                                "&.Mui-selected": {
                                    color: '#061522',
                                },
                            },
                            "& .MuiTabs-indicator": {
                                backgroundColor: '#148DFD',
                                height: '1px',
                            },
                        }}
                    >
                        <Tab label="Web View" />
                        <Tab label="Tab View" />
                        <Tab label="Mobile View" />
                    </Tabs>
                    <Typography
                        onClick={handleLiveView}
                        sx={{
                            color: '#148DFD',
                            fontSize: 14,
                            fontWeight: 500,
                            cursor: "pointer",
                            fontFamily: `${settings.font_family || "Inter"} !important`,
                        }}
                    >
                        Live view
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    maxWidth: previewMaxWidth,
                    mx: "auto",
                    transition: "max-width 0.3s ease",

                    "& .MuiTypography-root, & .MuiButton-root, & .MuiTab-root, & .MuiInputBase-input, & .MuiInputBase-root": {
                        fontFamily: `${settings.font_family || "Inter"} !important`,
                    }
                }}
            >
                <PreviewNavbar
                    brandColor={settings.brand_color || "#148DFD"}
                    accentColor={settings.accent_color || "#556575"}
                    logoPreview={settings.logoPreview}
                    businessName={settings.business_name}
                    fontFamily={settings.font_family || "Inter"}
                    loading={loading}
                    viewMode={viewMode}
                />
                <PreviewHero
                    brandColor={settings.brand_color || "#148DFD"}
                    accentColor={settings.accent_color || "#556575"}
                    fontFamily={settings.font_family || "Inter"}
                    viewMode={viewMode}
                    loading={loading}
                />
                <Box
                    sx={{
                        width: "100%",
                        borderRadius: "0 0 8px 8px",
                        border: "1px solid #E5E9EC",
                        backgroundColor: "#FFF",
                        p: viewMode === "mobile" ? 2 : 3,
                    }}
                >
                    <FeaturedArticles
                        brandColor={settings.brand_color}
                        accentColor={settings.accent_color}
                        articles={featuredArticles}
                        loading={shouldShowFeaturedLoading}
                        fontFamily={settings.font_family}
                        viewMode={viewMode}
                    />
                    <BrowseCategory
                        brandColor={settings.brand_color}
                        accentColor={settings.accent_color}
                        categories={publicCategories}
                        loading={categoriesLoading}
                        fontFamily={settings.font_family}
                        viewMode={viewMode}
                    />
                </Box>
            </Box>

        </Paper>


    )
}
